import { create } from 'zustand';
import { annotationDashboardServices } from '@/Services';
import {
	TAB_CONSOLE, TAB_CVAT, isAccessDeniedError
} from './annotationProductivity.config';

/**
 * Monotonic guard so a slow earlier request cannot overwrite a faster later one.
 * Filter changes fire fetches back to back and the responses are not ordered.
 */
let latestRequestId = 0;

const toDateParam = (date) => {
	if (!date) return undefined;
	const pad = (value) => String(value).padStart(2, '0');
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * Returns null when nothing was allocated — a row with no denominator has no
 * completion ratio, and 0% would read as "did nothing" for someone who annotated
 * thousands of product IDs.
 *
 * The result may exceed 100: `imagesAnnotated` is a completion-time snapshot from
 * CVAT while `imagesAllocated` is a live count of non-deleted dispatched scans,
 * which can fall below it afterwards. Capping is a display concern, so the raw
 * ratio is kept here and sorting stays truthful.
 */
const computePercentCompleted = (row) => {
	const allocated = row?.imagesAllocated ?? 0;
	if (allocated <= 0) return null;
	return ((row?.imagesAnnotated ?? 0) / allocated) * 100;
};

/**
 * Field mapping for GET /v2.1/annotation/dashboard/productivity. `userName`
 * arrives pre-resolved (displayName || username) and `clientId` is the real
 * customerId, so nothing here needs a fallback. Rows are human work only — the
 * BE excludes `createdBy IS NULL`, which is AI / undertraining output.
 */
const normalizeRow = (row) => {
	const percentCompleted = computePercentCompleted(row);
	return {
		userId: row?.userId ?? null,
		user: row?.userName ?? '-',
		customerId: row?.clientId ?? null,
		customer: row?.clientName ?? '-',
		imagesAllocated: row?.imagesAllocated ?? 0,
		imagesAnnotated: row?.imagesAnnotated ?? 0,
		shapesDrawn: row?.shapesDrawn ?? 0,
		productIdsAnnotated: row?.productIdsAnnotated ?? 0,
		productIdsReviewed: row?.productIdsReviewed ?? 0,
		percentCompleted,
		// Sorting needs a number; -1 groups the not-applicable rows together
		// instead of scattering them through the middle of the order.
		percentSort: percentCompleted === null ? -1 : percentCompleted,
	};
};

const normalizeResponse = (response) => {
	const data = response?.data ?? {};
	return {
		// Latest populated reportDate. Never recomputed on the client: after a
		// failed nightly run the page must not claim a freshness it cannot vouch for.
		asOf: data?.asOf ?? null,
		rows: (data?.rows ?? []).map(normalizeRow),
	};
};

const hasConsoleWork = (row) => {
	return (row?.productIdsAnnotated ?? 0) > 0 || (row?.productIdsReviewed ?? 0) > 0;
};

/**
 * Any CVAT metric qualifies, never `imagesAllocated` alone. A completed task can
 * carry real work with allocation 0 — its dispatched scans were deleted or never
 * linked — and that row must still reach the CVAT tab rather than vanish.
 */
const hasCvatWork = (row) => {
	return (row?.imagesAllocated ?? 0) > 0 ||
		(row?.imagesAnnotated ?? 0) > 0 ||
		(row?.shapesDrawn ?? 0) > 0;
};

const INITIAL_DATA = {
	asOf: null,
	rows: [],
};

export const useAnnotationProductivityStore = create((set, get) => ({
	...INITIAL_DATA,

	loading: false,
	loadFailed: false,
	accessDenied: false,
	activeTab: TAB_CONSOLE,
	appliedFilters: {},
	customerFilter: [],
	annotatorFilter: [],
	dateRange: {
		from: null,
		to: null,
	},

	setActiveTab: (activeTab) => set({
		activeTab,
	}),

	setAnnotatorFilter: (annotatorFilter) => set({
		annotatorFilter,
	}),

	/**
	 * Applied from the shared ImagesFinder drawer. Customer rides in through the
	 * drawer's one generic slot (`customExclusiveFilters`).
	 */
	applyFinderFilters: (request) => {
		set({
			appliedFilters: request,
			customerFilter: request?.customExclusiveFilters?.id ?? [],
			dateRange: {
				from: request?.dateRange?.from ?? null,
				to: request?.dateRange?.to ?? null,
			},
		});
		get().fetchProductivity();
	},

	fetchProductivity: async () => {
		const { dateRange } = get();
		latestRequestId += 1;
		const requestId = latestRequestId;
		set({
			loading: true,
			loadFailed: false,
			accessDenied: false,
		});
		try {
			// toQueryStringURL appends whatever it is handed, so an absent date
			// would travel as the literal string "undefined".
			const params = {};
			const from = toDateParam(dateRange.from);
			const to = toDateParam(dateRange.to);
			if (from) params.from = from;
			if (to) params.to = to;
			const response = await annotationDashboardServices.fetchProductivity({
				params,
			});
			if (requestId !== latestRequestId) return;
			// handleResponse resolves with {} rather than rejecting when the
			// session is invalid, so an auth failure would otherwise render as
			// "no data" instead of a failure.
			if (!response?.data) {
				set({
					...INITIAL_DATA,
					loading: false,
					loadFailed: true,
				});
				return;
			}
			set({
				...normalizeResponse(response),
				loading: false,
			});
		} catch (error) {
			if (requestId !== latestRequestId) return;
			// Clear rather than keep the previous result: this page is read for
			// payroll-adjacent decisions, so an empty table is safer than one
			// silently describing a different date range. No toast raised here --
			// handleResponse already shows one for any API-level failure, and the
			// table carries an inline message regardless.
			//
			// A refusal is not a transient failure: telling someone without access
			// to "try again" sends them round a loop that cannot succeed.
			set({
				...INITIAL_DATA,
				loading: false,
				loadFailed: true,
				accessDenied: isAccessDeniedError(error),
			});
		}
	},

	/**
	 * A row belongs to a tab only if it holds that kind of work. Keeping the
	 * others would pad both tables with zeroes and leave % Completed undefined
	 * on most CVAT rows.
	 */
	getVisibleRows: () => {
		const { rows, activeTab, customerFilter, annotatorFilter } = get();
		const belongsToTab = activeTab === TAB_CVAT ? hasCvatWork : hasConsoleWork;
		return rows.filter((row) => {
			if (!belongsToTab(row)) return false;
			const customerMatch = customerFilter.length === 0 ||
				customerFilter.includes(row.customerId);
			const annotatorMatch = annotatorFilter.length === 0 ||
				annotatorFilter.includes(row.userId);
			return customerMatch && annotatorMatch;
		});
	},

	getTotals: () => {
		return get().getVisibleRows().reduce((acc, row) => {
			return {
				imagesAllocated: acc.imagesAllocated + (row.imagesAllocated ?? 0),
				imagesAnnotated: acc.imagesAnnotated + (row.imagesAnnotated ?? 0),
				shapesDrawn: acc.shapesDrawn + (row.shapesDrawn ?? 0),
				productIdsAnnotated:
					acc.productIdsAnnotated + (row.productIdsAnnotated ?? 0),
				productIdsReviewed:
					acc.productIdsReviewed + (row.productIdsReviewed ?? 0),
			};
		}, {
			imagesAllocated: 0,
			imagesAnnotated: 0,
			shapesDrawn: 0,
			productIdsAnnotated: 0,
			productIdsReviewed: 0,
		});
	},

	/** Options come from the returned rows, keyed on the real ids. */
	getFilterOptions: () => {
		const { rows } = get();
		const customers = new Map();
		const annotators = new Map();
		rows.forEach((row) => {
			if (row?.customerId !== null) customers.set(row.customerId, row.customer);
			if (row?.userId !== null) annotators.set(row.userId, row.user);
		});
		const toSortedOptions = (source) => {
			return [...source.entries()]
				.map(([value, label]) => ({
					value,
					label,
				}))
				.sort((a, b) => String(a.label).localeCompare(String(b.label)));
		};
		return {
			customers: toSortedOptions(customers),
			annotators: toSortedOptions(annotators),
		};
	},
}));
